
  /**
   * @license
   * author: epignosis front-end team
   * @epignosis_llc/gnosis.js v6.0.0
   * Released under the MIT license.
   */

import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { forwardRef, useRef, useImperativeHandle, useEffect, isValidElement } from 'react';
import classNames from '../../../../node_modules/classnames/index.js';
import Label from '../Label/Label.js';
import SvgInfoCircled from '../../../icons/legacy/info-circled.svg.js';
import SvgClose from '../../../icons/legacy/close.svg.js';
import Tooltip from '../../Tooltip/Tooltip.js';
import { inputContainer } from './styles.js';
import { preventNonNumericalInput } from './helpers.js';
import { createElement as jsx } from '../../../../node_modules/@emotion/react/dist/emotion-react.browser.esm.js';

var _excluded = ["size", "status", "required", "iconBefore", "iconAfter", "label", "inline", "id", "containerAttrs", "tooltipContent", "tooltipIcon", "value", "isClearable", "showVerticalLine", "onClear", "autoFocus", "type", "onIconClick"];
var Input = function Input(_ref, forwardedRef) {
  var _ref$size = _ref.size,
    size = _ref$size === void 0 ? "md" : _ref$size,
    _ref$status = _ref.status,
    status = _ref$status === void 0 ? "valid" : _ref$status,
    _ref$required = _ref.required,
    required = _ref$required === void 0 ? false : _ref$required,
    iconBefore = _ref.iconBefore,
    iconAfter = _ref.iconAfter,
    label = _ref.label,
    _ref$inline = _ref.inline,
    inline = _ref$inline === void 0 ? false : _ref$inline,
    id = _ref.id,
    containerAttrs = _ref.containerAttrs,
    _ref$tooltipContent = _ref.tooltipContent,
    tooltipContent = _ref$tooltipContent === void 0 ? "" : _ref$tooltipContent,
    tooltipIcon = _ref.tooltipIcon,
    value = _ref.value,
    _ref$isClearable = _ref.isClearable,
    isClearable = _ref$isClearable === void 0 ? false : _ref$isClearable,
    _ref$showVerticalLine = _ref.showVerticalLine,
    showVerticalLine = _ref$showVerticalLine === void 0 ? true : _ref$showVerticalLine,
    onClear = _ref.onClear,
    _ref$autoFocus = _ref.autoFocus,
    autoFocus = _ref$autoFocus === void 0 ? false : _ref$autoFocus,
    type = _ref.type,
    onIconClick = _ref.onIconClick,
    rest = _objectWithoutProperties(_ref, _excluded);
  var internalRef = useRef(null);
  var isNumberType = type === "number";
  var IconBefore = iconBefore;
  var IconAfter = iconAfter;
  var hasLabel = Boolean(label);
  var containerClasses = classNames({
    valid: status === "valid",
    error: status === "error",
    "with-prefix-icon": Boolean(iconBefore),
    "with-suffix-icon": Boolean(iconAfter),
    inline: hasLabel && inline,
    disabled: Boolean(rest === null || rest === void 0 ? void 0 : rest.disabled)
  });
  var labelClassname = classNames({
    required: required
  });
  var iconHeight = size === "sm" ? 28 : 32;
  useImperativeHandle(forwardedRef, function () {
    return internalRef.current;
  });
  var setFocus = function setFocus() {
    var _internalRef$current;
    (_internalRef$current = internalRef.current) === null || _internalRef$current === void 0 || _internalRef$current.focus();
  };
  useEffect(function () {
    var _internalRef$current2;
    autoFocus && ((_internalRef$current2 = internalRef.current) === null || _internalRef$current2 === void 0 ? void 0 : _internalRef$current2.focus());
  }, [autoFocus]);
  var handleClear = function handleClear(e) {
    if (onClear) onClear(e);
    setFocus();
  };
  var shouldRenderTooltip = tooltipContent && typeof tooltipContent === "string" && tooltipContent !== "" || /*#__PURE__*/isValidElement(tooltipContent);
  var hasOnIconClick = Boolean(onIconClick);
  return jsx("div", _extends({
    css: function css(theme) {
      return inputContainer(theme, {
        size: size,
        hasIconAfter: Boolean(iconAfter),
        isClearable: isClearable,
        showVerticalLine: showVerticalLine,
        hasOnIconClick: hasOnIconClick
      });
    },
    className: containerClasses
  }, containerAttrs), hasLabel && jsx("div", {
    className: "label-container"
  }, jsx(Label, {
    htmlFor: id,
    className: labelClassname,
    "data-testid": "".concat(id, "-label")
  }, label), shouldRenderTooltip && jsx("div", {
    "data-testid": "".concat(id, "-tooltip")
  }, jsx(Tooltip, {
    content: tooltipContent,
    parentProps: {
      "aria-label": label
    }
  }, tooltipIcon || jsx(SvgInfoCircled, {
    height: 20
  })))), jsx("div", {
    className: "input-wrapper"
  }, IconBefore && jsx("span", {
    className: "prefix-icon",
    "data-testid": "input-icon-before"
  }, jsx(IconBefore, {
    height: iconHeight,
    onClick: onIconClick
  })), jsx("input", _extends({
    value: value,
    ref: internalRef,
    id: id,
    onKeyDown: function onKeyDown(e) {
      return preventNonNumericalInput(e, isNumberType);
    },
    type: type,
    "data-testid": "".concat(id, "-input")
  }, rest)), IconAfter && jsx(React__default.Fragment, null, showVerticalLine && jsx("div", {
    className: "vertical-line"
  }), jsx("span", {
    className: "suffix-icon",
    "data-testid": "input-icon-after",
    onClick: setFocus
  }, jsx(IconAfter, {
    height: iconHeight,
    onClick: onIconClick
  }))), isClearable && value && jsx("div", {
    className: "close-icon",
    onClick: function onClick(e) {
      return handleClear(e);
    }
  }, jsx(SvgClose, {
    height: 16
  }))));
};
var Input$1 = /*#__PURE__*/forwardRef(Input);

export { Input$1 as default };
//# sourceMappingURL=Input.js.map
